import { type AgChartThemeOverrides } from "ag-charts-community/typings/main"
import { dset } from "dset"
import type {
  ColDef,
  Column,
  ValueFormatterFunc,
  ValueFormatterParams,
} from "@ag-grid-community/core"

/*
 * General note about embedded charts in the context of dynamic data where we use them:
 * Everything here is basically hacking and we can't really help it.
 */

type TooltipRendererResult = Partial<{
  enabled: boolean
  // Set to false to disable individual tooltip.
  content: string
  // The content to display in each tooltip.
  title: string
  // The title of the tooltip.
  color: string
  // The CSS color for the title text.
  backgroundColor: string
  // The CSS color for the background of the tooltip title.
  opacity: number
  // The opacity of the background for the tooltip title.
}>

type PieChartProps = {
  angleName?: string
  angleKey?: string
  angleValue?: any
  sectorLabelKey?: string
  sectorLabelName?: string
  sectorLabelValue?: any
  datum?: Record<string, any>
  calloutLabelKey?: string
}

const dummyColDef: ColDef = {}
/*
 * Somewhat dummy parser for valueFormatter params to be passed
 * This exists because eg. it's not correct to start handling cases like "colDef" not being defined for valueFormatter, which it should have
 * but for which we don't necessarily have access here or need, but which we require in formatter.
 * Currently we require "colDef" in valueFormatters due to €/$ -separation based on "field"
 * If we don't pass any colDef, it silently fails the formatting and throws error in console only
 */
const parseFormatterParams = (
  params: Partial<ValueFormatterParams<any, any>> &
    Pick<ValueFormatterParams<any, any>, "value">
) => {
  if (!params.colDef) {
    params.colDef = dummyColDef
  }
  return params as ValueFormatterParams<any, any>
}

const createAxisFormatter =
  (formatter: ValueFormatterFunc) =>
  (params: { value?: number | string | null }) => {
    const val =
      typeof params.value === "number"
        ? params.value
        : Number.parseInt(params.value ?? "", 10)
    return formatter(parseFormatterParams({ value: val }))
  }

const createScatterAxisFormatter =
  (formatter: ValueFormatterFunc, categoryFormatter: ValueFormatterFunc) =>
  ({ value }: { value?: number | string | null }) => {
    // This is funny. Scatter doesn't allow separating Y and X axis label formatters if and when both are showing "number" data.
    if (typeof value === "number") {
      // If this number axis formatter is being used on number values, it's the Y axis and to be formatter based on primary column so to speak
      // NOTE: There's unfortunately a bug with this one as "time" is also of type number, so this wont work for it. Not much we can do about it, but luckily not that many metrics use "time"
      return formatter(parseFormatterParams({ value }))
    } else if (typeof value === "string" && value) {
      // If the value is string, this is a category/time axis in reality, so the X-axis.
      return categoryFormatter(
        parseFormatterParams({
          value: Number.parseInt(value, 10),
        })
      )
    }

    return formatter({ value: null } as ValueFormatterParams<any, any>)
  }

// MUTATES overrides on purpose
/** @deprecated */
export const resolveChartThemeOverrides = <T extends unknown = any>(
  overrides: AgChartThemeOverrides,
  chartValueColumns: Column<T>[]
): AgChartThemeOverrides => {
  // Consider first value column as primary (refers to primary axis). Can be undefined when cellRange has no value cells
  const [primaryValueColumn, secondaryValueColumn] = chartValueColumns

  const {
    cellDataType: primaryAxisDataType,
    valueFormatter: primaryAxisValueFormatter,
  } = primaryValueColumn?.getColDef() ?? {}

  const {
    //
    valueFormatter: secondaryAxisValueFormatter,
  } = secondaryValueColumn?.getColDef() ?? {}

  /* NOTE: We mutate chartThemeOverrides. This is because alternative is to use onChartOptionsChanged which re-triggers
   * itself when calling updateChart and alters Format menu UX elements behaviour. It doesn't feel the way to go
   * If mutating causes issues, maybe we should store overridees per grid into a ref and mutate that.
   */
  const themeOverrides = overrides

  const axes = themeOverrides.common.axes ?? {}

  if (typeof primaryAxisValueFormatter === "function") {
    // Primary axis is number axis, aka left side (in LTR mode) yAxis. AG grid naming convention in docs (they speak of secondary axis)
    // TODO: somehow pass 'field' information to formatter too so we can format eg. € instead of $ based on field's name
    dset(
      axes,
      "number.label.formatter",
      createAxisFormatter(primaryAxisValueFormatter)
    )
  }

  // TODO: while moving this function and others to new file, also should refactor the formatters to be static which just get the chartValueColumns as param
  const tooltipRenderer = function ({
    title,
    yValue,
    yKey,
    angleName,
    angleKey,
    angleValue,
    calloutLabelKey,
    datum,
  }: {
    title?: string
    yKey: string
    yValue?: any
    // And some more
  } & PieChartProps): TooltipRendererResult {
    // Pie/doughnut uses angle prefixed keys + calloutLabel and rest of the charts yBased + title
    const id = yKey ?? angleKey
    const value = yValue ?? angleValue
    const name = title ?? angleName
    const serieLabel = calloutLabelKey ? datum?.[calloutLabelKey] : ""

    const column = chartValueColumns.find((column) => column.getColId() === id)
    // TODO: create custom tooltip and mimic current chart tooltip (or preferably WEB-1237) as much as makes sense and is possible
    const formatter = column?.getColDef().valueFormatter
    const parentGroup = column?.getParent()?.getColGroupDef()

    let parsedValue: number | string
    try {
      if (typeof value === "number") {
        parsedValue = value
      } else if (typeof value === "string" && value) {
        // probably never
        parsedValue = Number.parseInt(value, 10)
        if (isNaN(parsedValue)) {
          throw "Null value"
        }
      } else {
        throw "Null value"
      }
    } catch (error) {
      parsedValue = "N/A"
    }

    const valueLabel = parentGroup?.headerName
      ? `${parentGroup.headerName} ${name?.toLowerCase() ?? ""}`
      : name
    const formattedValue =
      typeof formatter === "function" && typeof parsedValue === "number"
        ? formatter(parseFormatterParams({ value: parsedValue }))
        : parsedValue

    return {
      title: serieLabel || valueLabel,
      content: `${serieLabel ? `${valueLabel}: ` : ""}${formattedValue}`,
    }
  }

  const legendLabelFormatter = ({
    value,
    itemId,
    angleKey,
  }: { seriesId: string; itemId: any; value: string } & PieChartProps) => {
    const id = angleKey ?? itemId
    const parentGroup = chartValueColumns
      .find((column) => column.getColId() === id)
      ?.getParent()
      ?.getColGroupDef()

    if (parentGroup?.headerName) {
      return `${parentGroup.headerName} ${value.toLowerCase()}`
    }
    return value
  }

  const pieChartSectorLabelFormatter = function ({
    sectorLabelKey,
    sectorLabelValue,
  }: PieChartProps): string {
    /*
     * NOTE: not really possible to display percentage share here without populating it into the data first.
     * The props do have "angleValue", which is a share, but it's a cumulative one. Only the first serie gets correct share value.
     * Also the series render from small to big, so we can't trivially gather series data and reduce it to produce more meaningful values.
     */
    if (typeof sectorLabelValue !== "number" || !sectorLabelKey) {
      return sectorLabelValue
    }
    const column = chartValueColumns.find(
      (column) => column.getColId() === sectorLabelKey
    )
    const formatter = column?.getColDef().valueFormatter

    return `${
      typeof formatter === "function"
        ? formatter(parseFormatterParams({ value: sectorLabelValue }))
        : sectorLabelValue
    }`
  }

  dset(themeOverrides, "common.axes", axes)
  dset(
    themeOverrides,
    "common.legend.item.label.formatter",
    legendLabelFormatter
  )
  dset(themeOverrides, "column.series.tooltip.renderer", tooltipRenderer)
  dset(themeOverrides, "bar.series.tooltip.renderer", tooltipRenderer)
  dset(themeOverrides, "line.series.tooltip.renderer", tooltipRenderer)
  dset(themeOverrides, "area.series.tooltip.renderer", tooltipRenderer)
  dset(themeOverrides, "pie.series.tooltip.renderer", tooltipRenderer)
  dset(
    themeOverrides,
    "pie.series.sectorLabel.formatter",
    pieChartSectorLabelFormatter
  )

  dset(themeOverrides, "scatter.series.tooltip.renderer", tooltipRenderer)

  if (
    typeof secondaryAxisValueFormatter === "function" &&
    typeof primaryAxisValueFormatter === "function"
  ) {
    // Very important for scatter to work
    dset(
      themeOverrides,
      "scatter.axes.number.label.formatter",
      createScatterAxisFormatter(
        secondaryAxisValueFormatter,
        primaryAxisValueFormatter
      )
    )
  }

  return themeOverrides
  /* NOTE: Having 2 axises could go like this. However for whatever reason this doesn't seem to work due to theme setup. This code is probably fine
   * The task of 2 axises is abandoned due to technical issues and also realising it probably makes only sense in limited cases if even in those
   */
  // const isInNeedOfSecondaryAxis = chartValueColumns.some((col) => {
  //   const colDef = col.getColDef()
  //   return colDef.cellDataType !== primaryAxisDataType && typeof colDef.valueFormatter === "function"
  // })
  // let secondaryAxis
  // let bottomAxis
  // if (isInNeedOfSecondaryAxis) {
  //   // Need to spin up secondary axis and modify primary to dual axis setup
  //   primaryAxis.position = "left"
  //   primaryAxis.type = "number"
  //   primaryAxis.keys = chartValueColumns.filter((col) => col.getColDef().cellDataType === primaryAxisDataType).map((col) => col.getColId())

  //   // Also setup bottom axis for category
  //   bottomAxis = {
  //     ...chartThemeOverrides.common.axes.category,
  //     type: "category",
  //     position: "bottom"
  //   }

  //   const { cellDataType: secondaryAxisDataType, valueFormatter: secondaryAxisValueFormatter } = chartValueColumns.find((col) => {
  //     const colDef = col.getColDef()
  //     return colDef.cellDataType !== primaryAxisDataType && typeof colDef.valueFormatter === "function"
  //   })!.getColDef()

  //   secondaryAxis = {
  //     ...chartThemeOverrides.common.axes,
  //     position: "right",
  //     type: "number",
  //     keys: chartValueColumns.filter((col) => col.getColDef().cellDataType === secondaryAxisDataType).map((col) => col.getColId())
  //   }

  //   // Maybe always true, so could be just considered for types atm.
  //   if (typeof secondaryAxisValueFormatter === "function") {
  //     secondaryAxis = {
  //       ...secondaryAxis,
  //       number: {
  //         ...secondaryAxis.number,
  //         label: {
  //           ...secondaryAxis.number.label,
  //           formatter: (params: { value?: number | null }) =>
  //             secondaryAxisValueFormatter({ value: Number.parseInt(params.value, 10) }),
  //         }
  //       }
  //     }
  //   }
  // }
}
